// src/utilidades/pedidosLocales.js
// «Mis pedidos» en este dispositivo: la clave privada de cada pedido (UUID), un
// resumen para listarlos y el último detalle visto, para mostrarlo sin consultar.
// Solo localStorage del comprador: 0 lecturas. La señal del pedido (useSenalPedido)
// avisa si hay que traer el detalle de nuevo. La clave nunca se envía a terceros.
import { clavePedidoValida } from './seguimientoPedido';

const MAX_PEDIDOS = 20;
const DIAS_VIGENCIA = 180;
const MS_DIA = 24 * 60 * 60 * 1000;
const clave = gymId => `agPedidos_${gymId}`;

const limpiar = (p) => ({
  clave: p.clave,
  numeroPedido: typeof p.numeroPedido === 'string' ? p.numeroPedido.slice(0, 40) : '',
  totalCentimos: Number.isFinite(p.totalCentimos) ? p.totalCentimos : null,
  estado: typeof p.estado === 'string' ? p.estado.slice(0, 40) : '',
  version: Number.isFinite(p.version) ? p.version : 0,
  creado: Number.isFinite(p.creado) ? p.creado : Date.now(),
});

export const leerPedidosLocales = (gymId) => {
  try {
    const lista = JSON.parse(localStorage.getItem(clave(gymId)) || '[]');
    const limite = Date.now() - DIAS_VIGENCIA * MS_DIA;
    return (Array.isArray(lista) ? lista : [])
      .filter(p => p && clavePedidoValida(p.clave) && (!Number.isFinite(p.creado) || p.creado >= limite))
      .map(limpiar).sort((a, b) => b.creado - a.creado).slice(0, MAX_PEDIDOS);
  } catch { return []; }
};

const escribir = (gymId, lista) => {
  try { localStorage.setItem(clave(gymId), JSON.stringify(lista.slice(0, MAX_PEDIDOS))); } catch { /* sin almacenamiento */ }
};

/** Agrega o actualiza un pedido (por su clave). Devuelve la lista resultante. */
export const guardarPedidoLocal = (gymId, pedido) => {
  if (!clavePedidoValida(pedido?.clave)) return leerPedidosLocales(gymId);
  const actuales = leerPedidosLocales(gymId);
  const previo = actuales.find(p => p.clave === pedido.clave);
  const nuevo = limpiar({ ...previo, ...Object.fromEntries(Object.entries(pedido).filter(([, v]) => v !== undefined && v !== null && v !== '')) });
  const lista = [nuevo, ...actuales.filter(p => p.clave !== pedido.clave)].sort((a, b) => b.creado - a.creado);
  escribir(gymId, lista);
  return lista.slice(0, MAX_PEDIDOS);
};

export const quitarPedidoLocal = (gymId, claveP) => {
  const lista = leerPedidosLocales(gymId).filter(p => p.clave !== claveP);
  escribir(gymId, lista);
  try { localStorage.removeItem(claveDetalle(gymId, claveP)); } catch { /* sin almacenamiento */ }
  return lista;
};

// Último detalle visto de cada pedido, sin los datos de cobro (van una vez por tienda).
const claveDetalle = (gymId, claveP) => `agPedido_${gymId}_${claveP}`;
export const leerDetallePedido = (gymId, claveP) => {
  try { const d = JSON.parse(localStorage.getItem(claveDetalle(gymId, claveP)) || 'null'); return d && typeof d === 'object' ? d : null; }
  catch { return null; }
};
export const guardarDetallePedido = (gymId, claveP, vista) => {
  if (!clavePedidoValida(claveP) || !vista) return;
  const { cobroManual, qrYape, ...detalle } = vista;
  try { localStorage.setItem(claveDetalle(gymId, claveP), JSON.stringify(detalle)); } catch { /* sin almacenamiento */ }
  if (cobroManual) guardarCobroTienda(gymId, cobroManual);
};

// Datos para pagar (Yape, Plin, titular, QR) de la tienda: se piden una sola vez.
const claveCobro = gymId => `agCobro_${gymId}`;
export const leerCobroTienda = (gymId) => {
  try { const c = JSON.parse(localStorage.getItem(claveCobro(gymId)) || 'null'); return c && typeof c === 'object' ? c : null; }
  catch { return null; }
};
export const guardarCobroTienda = (gymId, cobro) => {
  try { localStorage.setItem(claveCobro(gymId), JSON.stringify(cobro)); } catch { /* sin almacenamiento */ }
};

/** Resumen local a partir de la respuesta pública de ordenOperacion. */
export const resumenPedido = (claveP, res) => ({
  clave: claveP, numeroPedido: res?.numeroPedido || res?.id || '', totalCentimos: res?.totalCentimos,
  estado: res?.estado, version: Number.isFinite(res?.version) ? res.version : undefined,
  creado: Number.isFinite(res?.creado) ? res.creado : undefined,
});
